import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography } from '@mui/material';

/**
 * Helper to get card image path
 */
const getCardImagePath = (suit, value) => {
    const suitMap = {
        'oros': 1,
        'copas': 2,
        'espadas': 3,
        'bastos': 4
    };
    const suitCode = suitMap[suit];
    const valueCode = value.toString().padStart(2, '0');
    return `/assets/images/deck/${suitCode}${valueCode}.png`;
};

/**
 * Get display name for card value
 */
const getValueLabel = (value) => {
    const labels = {
        1: 'As',
        2: '2',
        3: '3',
        4: '4',
        5: '5',
        6: '6',
        7: '7',
        10: 'Sota',
        11: 'Caballo',
        12: 'Rey'
    };
    return labels[value] || value;
};

/**
 * Dialog for selecting the value of a Wildcard (Comodín)
 * Vertical layout: top row shows player's cards, bottom grid shows value options
 */
const WildcardSelector = ({ open, onClose, onSelect, card, currentValue, playerCards = [] }) => {
    // Values allowed in Baciga: 1-7, 10, 11, 12
    const values = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12];
    const topRow = values.slice(0, 5);    // 1-5
    const bottomRow = values.slice(5);     // 6, 7, 10, 11, 12

    const handleSelect = (val) => {
        onSelect(val);
        onClose();
    };

    // Use oros for the value selection cards
    const sampleSuit = 'oros';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    margin: 1,
                    background: 'linear-gradient(135deg, #1a472a 0%, #2d5a3d 50%, #1a472a 100%)',
                    color: 'white',
                }
            }}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'flex-start',
                    paddingTop: '2vh',
                },
            }}
        >
            <DialogTitle sx={{
                textAlign: 'center',
                py: 1,
                fontSize: '1rem',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
            }}>
                Elegir valor para el comodín
            </DialogTitle>

            <DialogContent sx={{ pt: 0.5, pb: 0.5, overflow: 'visible' }}>
                {/* Top section - Player's cards (horizontal scrollable row) */}
                <Typography
                    variant="body2"
                    sx={{
                        mb: 0.5,
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                    }}
                >
                    Tus cartas
                </Typography>
                <Box sx={{
                    display: 'flex',
                    gap: 0.5,
                    overflowX: 'auto',
                    pb: 0.5,
                    mb: 1.5,
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        borderRadius: 2,
                    },
                }}>
                    {playerCards.map((c, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                flexShrink: 0,
                                position: 'relative',
                                border: c === card ? '2px solid #f57c00' : '1px solid rgba(255,255,255,0.3)',
                                borderRadius: 1,
                                overflow: 'hidden',
                                boxShadow: c === card ? '0 0 6px rgba(245, 124, 0, 0.5)' : 1,
                            }}
                        >
                            <img
                                src={getCardImagePath(c.suit, c.assignedValue || c.value)}
                                alt={`${c.value} de ${c.suit}`}
                                style={{
                                    height: 50,
                                    width: 'auto',
                                    display: 'block'
                                }}
                            />
                            {c.assignedValue && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        bgcolor: 'rgba(245, 124, 0, 0.9)',
                                        color: 'white',
                                        fontSize: '0.5rem',
                                        textAlign: 'center',
                                        py: 0.1
                                    }}
                                >
                                    ={getValueLabel(c.assignedValue)}
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>

                {/* Bottom section - Value selector grid (2 rows x 5 columns) */}
                <Typography
                    variant="body2"
                    sx={{
                        textAlign: 'center',
                        mb: 0.5,
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                    }}
                >
                    Selecciona el valor
                </Typography>
                {[topRow, bottomRow].map((row, rowIdx) => (
                    <Box
                        key={rowIdx}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 0.75,
                            mb: rowIdx === 0 ? 0.75 : 0,
                        }}
                    >
                        {row.map((val) => (
                            <Box
                                key={val}
                                onClick={() => handleSelect(val)}
                                sx={{
                                    flex: '1 1 0',
                                    maxWidth: 60,
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    border: currentValue === val
                                        ? '3px solid #4caf50'
                                        : '2px solid rgba(255,255,255,0.3)',
                                    boxShadow: currentValue === val
                                        ? '0 0 10px rgba(76, 175, 80, 0.5)'
                                        : 2,
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                        boxShadow: 4,
                                        borderColor: 'rgba(255,255,255,0.6)',
                                    },
                                    '&:active': {
                                        transform: 'scale(0.95)',
                                    },
                                }}
                            >
                                <img
                                    src={getCardImagePath(sampleSuit, val)}
                                    alt={getValueLabel(val)}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        display: 'block',
                                    }}
                                />
                                <Box
                                    sx={{
                                        bgcolor: currentValue === val ? '#4caf50' : 'rgba(255,255,255,0.15)',
                                        color: 'white',
                                        textAlign: 'center',
                                        py: 0.25,
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                    }}
                                >
                                    {getValueLabel(val)}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ))}
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', py: 0.5 }}>
                <Button onClick={onClose} sx={{ color: 'white' }} size="small">Cancelar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default WildcardSelector;
